import { z } from 'zod';
import { apiUrl } from '../config/api';
import { apiClient } from '../lib/apiClient';
import type {
  ProjectPropertyRow,
  ProjectPropertyTableConfig,
} from '../lib/projectPropertiesConfig';
import { envelopeSchema } from '../lib/schemas';

export type ProjectPropertyInput = Record<string, string | undefined>;

function tableUrl(config: ProjectPropertyTableConfig, id?: string): string {
  const base = `${apiUrl.projectProperties()}/${config.apiSegment}`;
  if (id) return `${base}/${id}`;
  return base;
}

export const projectPropertiesService = {
  async getAll(config: ProjectPropertyTableConfig): Promise<ProjectPropertyRow[]> {
    const result = await apiClient.get(tableUrl(config), {
      validate: envelopeSchema(config.listEnvelopeKey, z.array(config.rowSchema)) as z.ZodType<
        Record<string, ProjectPropertyRow[]>
      >,
      deduplicate: true,
      context: `getProjectProperties:${config.key}`,
    });
    return result[config.listEnvelopeKey] as ProjectPropertyRow[];
  },

  async create(
    config: ProjectPropertyTableConfig,
    row: ProjectPropertyInput
  ): Promise<ProjectPropertyRow> {
    const result = await apiClient.post(tableUrl(config), row, {
      validate: envelopeSchema(config.singleEnvelopeKey, config.rowSchema) as z.ZodType<
        Record<string, ProjectPropertyRow>
      >,
      context: `createProjectProperty:${config.key}`,
    });
    return result[config.singleEnvelopeKey] as ProjectPropertyRow;
  },

  async update(
    config: ProjectPropertyTableConfig,
    id: string,
    row: ProjectPropertyInput
  ): Promise<ProjectPropertyRow> {
    const result = await apiClient.put(tableUrl(config, id), row, {
      validate: envelopeSchema(config.singleEnvelopeKey, config.rowSchema) as z.ZodType<
        Record<string, ProjectPropertyRow>
      >,
      context: `updateProjectProperty:${config.key}`,
    });
    return result[config.singleEnvelopeKey] as ProjectPropertyRow;
  },

  async delete(config: ProjectPropertyTableConfig, id: string): Promise<void> {
    await apiClient.delete(tableUrl(config, id), {
      context: `deleteProjectProperty:${config.key}`,
    });
  },
};
